import React, { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiArrowRight,
  FiArrowUpRight,
  FiMail,
  FiClock,
  FiMessageCircle,
  FiHeart,
  FiCode,
  FiMousePointer,
} from "react-icons/fi";
import {
  FaCode,
  FaLaptopCode,
  FaVideo,
  FaPalette,
  FaPenNib,
  FaTools,
  FaWhatsapp,
} from "react-icons/fa";
import Navbar from "./Navbar";
import Foot from "./Foot";
import "./Hero.css";      // design system: tokens, buttons, .kk-services, .kk-preview
import "./Services.css";  // everything unique to this page

/* ═══════════════ DATA ═══════════════ */

const SERVICES = [
  {
    icon: <FaLaptopCode />,
    title: "Full Stack Web Development",
    desc: "Complete websites with frontend + backend + authentication + database. End-to-end solutions built with modern technologies.",
    features: ["React / Next.js", "Node.js / Express", "MongoDB / SQL", "Firebase Auth"],
    preview: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRPV0S6QPMqXN7rCVsTPsQzKSWcb2mpBU6Z4Q&s",
    fallback: "/img/about-workspace.jpg",
    color: "#6366f1",
  },
  {
    icon: <FaCode />,
    title: "Frontend Development",
    desc: "Responsive UI, smooth animations, and modern UX design. Pixel-perfect implementations that delight users.",
    features: ["Responsive Design", "CSS Animations", "Component Libraries", "Performance"],
    preview: "https://upscalenagpur.co.in/wp-content/uploads/2024/02/The-Frontend-Developer-Roadmap-for-Beginners-in-2024.webp",
    fallback: "/img/proj-products.jpg",
    color: "#06b6d4",
  },
  {
    icon: <FaTools />,
    title: "Backend & API",
    desc: "Secure APIs, authentication systems, database design, and cloud deployment for scalable applications.",
    features: ["REST APIs", "Authentication", "Database Design", "Cloud Deploy"],
    preview: "https://devpractical.com/public/2023/backend-developer.svg",
    fallback: "/img/proj-realtycore.jpg",
    color: "#a855f7",
  },
  {
    icon: <FaPalette />,
    title: "Poster Designing",
    desc: "Creative posters for social media, events, and branding. Eye-catching designs that make an impact.",
    features: ["Social Media", "Event Posters", "Brand Identity", "Print Ready"],
    preview: "https://bcassetcdn.com/assets/images/designcom/maker-home-v2/poster-maker-4@2x.en-us.png",
    fallback: "/img/proj-listing.jpg",
    color: "#f43f5e",
  },
  {
    icon: <FaPenNib />,
    title: "Card Designing",
    desc: "Professional business cards and invitation designs. Elegant layouts that leave a lasting impression.",
    features: ["Business Cards", "Invitations", "Logo Design", "Brand Kit"],
    preview: "https://prod.superblogcdn.com/site_cuid_cljwev9k51068503tp9cdl2howu/images/visiting-card-1704947099753-compressed.png",
    fallback: "/img/proj-aushlink.jpg",
    color: "#f59e0b",
  },
  {
    icon: <FaVideo />,
    title: "Video Editing",
    desc: "Reels, YouTube edits, and cinematic videos. Professional editing that tells your story.",
    features: ["Reels & Shorts", "YouTube Edits", "Cinematic", "Motion Graphics"],
    preview: "https://rwxrdxvxndclnqvznxfj.supabase.co/storage/v1/object/public/rve-version-6/version-8-min.png",
    fallback: "/img/proj-darkhackers.jpg",
    color: "#22c55e",
  },
];

const WHY = [
  { icon: <FiCode />, title: "Quality code", desc: "Clean, maintainable, documented." },
  { icon: <FiClock />, title: "On time", desc: "Deadlines are part of the brief." },
  { icon: <FiMessageCircle />, title: "Clear comms", desc: "Regular updates, no surprises." },
  { icon: <FiHeart />, title: "100% satisfaction", desc: "Built to be signed off, not settled for." },
];

const STEPS = [
  { no: "01", title: "Discover", desc: "We talk through the goal, the audience and what success looks like." },
  { no: "02", title: "Design", desc: "Wireframe and interface direction — agreed before a line of code." },
  { no: "03", title: "Build", desc: "Clean components, real data, plain-language progress updates." },
  { no: "04", title: "Ship", desc: "Deploy, hand over, and stay on for fixes and small improvements." },
];

/* ═══════════════ ONE rAF LOOP — [data-plx] ═══════════════ */

function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    let raf = 0;

    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const r = el.getBoundingClientRect();
        if (r.bottom < -420 || r.top > vh + 420) continue;
        const y = (r.top + r.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* ═══════════════ PARTICLES ═══════════════ */

function Particles() {
  const parts = useRef(
    Array.from({ length: 22 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="sv-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="sv-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

/* ═══════════════ PAGE ═══════════════ */

export default function Services() {
  const rootRef = useRef(null);
  const previewRef = useRef(null);

  useParallax(rootRef);

  /* cursor-following preview — same behaviour as the Home services list */
  const showPreview = (service, e) => {
    const el = previewRef.current;
    if (!el || window.innerWidth <= 1024) return;
    const img = el.querySelector("img");
    el.dataset.fallback = service.fallback;
    img.onerror = () => {
      if (img.src.indexOf(el.dataset.fallback) === -1) img.src = el.dataset.fallback;
    };
    img.src = service.preview;
    el.querySelector("span").textContent = service.title;
    el.style.setProperty("--pv", service.color);
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
    el.classList.add("is-on");
  };
  const movePreview = (e) => {
    const el = previewRef.current;
    if (!el) return;
    el.style.left = `${e.clientX}px`;
    el.style.top = `${e.clientY}px`;
  };
  const hidePreview = () => previewRef.current?.classList.remove("is-on");

  const mailFor = (title) =>
    `mailto:karanksxxx@gmail.com?subject=Service Request: ${encodeURIComponent(
      title
    )}&body=Hi, I'm interested in your ${encodeURIComponent(title)} service.`;

  return (
    <div className="kk-root sv-page" ref={rootRef}>
      <motion.div
        className="kk-progress"
        style={{
          scaleX: 0,
          display: "none",
        }}
      />
      <Particles />

      {/* ══════════════ HERO ══════════════ */}
      <header className="sv-hero">
        <div className="sv-hero__bg" aria-hidden="true">
          <span className="sv-hero__grid" data-plx="0.05" />
          <span className="sv-hero__glow sv-hero__glow--acid" data-plx="0.16" />
          <span className="sv-hero__glow sv-hero__glow--violet" data-plx="0.24" />
          <span className="sv-hero__word" data-plx="0.1">
            SERVICES
          </span>
        </div>

        <div className="kk-wrap sv-hero__in">
          <motion.div
            className="sv-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="sv-kicker">
              <span className="kk-dot" />
              <span>Services</span>
              <span className="kk-slash">/</span>
              <span>What I do</span>
            </div>

            <h1 className="sv-title">
              <motion.span
                className="sv-title__line"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
              >
                My{" "}
                <span className="sv-title__serif kk-serif kk-acid">Services</span>
              </motion.span>
              <motion.span
                className="sv-title__sub"
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.14, ease: [0.22, 1, 0.36, 1] }}
              >
                design, build, ship — one person.
              </motion.span>
            </h1>

            <p className="sv-lead">
              Bringing your ideas to life with clean code, creative design and modern
              technology — from a single landing page to a full product with auth,
              database and deployment.
            </p>

            <div className="sv-actions">
              <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Project Inquiry">
                Start a project <FiArrowRight />
              </a>
              <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/projects">
                See the work <FiArrowUpRight />
              </a>
            </div>

            <div className="sv-meta">
              <div>
                <b>06</b>
                <span>Services</span>
              </div>
              <div>
                <b>24h</b>
                <span>Reply time</span>
              </div>
              <div>
                <b>1+</b>
                <span>Years building</span>
              </div>
            </div>
          </motion.div>
        </div>
      </header>

      {/* ══════════════ SERVICES — same list as Home ══════════════ */}
      <section className="kk-section sv-list" id="services">
        <div className="kk-wrap">
          <div className="kk-sec-head">
            <div>
              <span className="kk-eyebrow kk-eyebrow--violet">01 — Services</span>
              <h2 className="kk-h2">
                What I can
                <br />
                <span className="kk-serif">build for you</span>
              </h2>
            </div>
            <div className="kk-sec-head__aside">
              <p>
                Six ways I can help — hover a row to see the kind of work it covers.
              </p>
              <span className="sv-hint">
                <FiMousePointer /> Hover for a preview
              </span>
            </div>
          </div>

          <div className="kk-services">
            {SERVICES.map((s, i) => (
              <motion.a
                className="kk-service"
                key={s.title}
                href={mailFor(s.title)}
                style={{ "--acc": s.color }}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ delay: (i % 3) * 0.06, duration: 0.5 }}
                onMouseEnter={(e) => showPreview(s, e)}
                onMouseMove={movePreview}
                onMouseLeave={hidePreview}
              >
                <span className="kk-service__idx">{String(i + 1).padStart(2, "0")}</span>
                <span className="kk-service__title">
                  <span className="kk-service__ic">{s.icon}</span>
                  {s.title}
                </span>
                <span className="sv-row__body">
                  <span className="kk-service__desc">{s.desc}</span>
                  <span className="sv-row__tags">
                    {s.features.map((f) => (
                      <em key={f}>{f}</em>
                    ))}
                  </span>
                </span>
                <span className="kk-service__arrow">
                  <FiArrowUpRight />
                </span>
              </motion.a>
            ))}
          </div>
        </div>

        <div className="kk-preview" ref={previewRef} aria-hidden="true">
          <img src="/img/about-workspace.jpg" alt="" />
          <span>Full Stack Web Development</span>
        </div>
      </section>

      {/* ══════════════ WHY ME — full-bleed strip ══════════════ */}
      <section className="sv-why" aria-label="Why work with me">
        {WHY.map((w, k) => (
          <motion.div
            key={w.title}
            className="sv-why__item"
            initial={{ opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ delay: k * 0.07, duration: 0.5 }}
          >
            <span className="sv-why__ic">{w.icon}</span>
            <h4>{w.title}</h4>
            <p>{w.desc}</p>
            <span className="sv-why__no">0{k + 1}</span>
          </motion.div>
        ))}
      </section>

      {/* ══════════════ PROCESS ══════════════ */}
      <section className="sv-process">
        <div className="sv-process__bg" aria-hidden="true">
          <span className="sv-process__orb" data-plx="0.14" />
          <span className="sv-process__grid" data-plx="0.05" />
        </div>

        <div className="kk-wrap">
          <div className="kk-sec-head">
            <div>
              <span className="kk-eyebrow">02 — Process</span>
              <h2 className="kk-h2">
                How the work
                <br />
                <span className="kk-serif kk-acid">actually goes</span>
              </h2>
            </div>
            <div className="kk-sec-head__aside">
              <p>Four steps, no mystery. You always know what is happening and what is next.</p>
            </div>
          </div>

          <div className="sv-steps">
            <span className="sv-steps__line" aria-hidden="true" />
            {STEPS.map((s, k) => (
              <motion.div
                key={s.no}
                className="sv-step"
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ delay: k * 0.1, duration: 0.55 }}
              >
                <span className="sv-step__dot" />
                <span className="sv-step__no" data-plx="0.03">
                  {s.no}
                </span>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════ CTA ══════════════ */}
      <section className="sv-cta">
        <div className="sv-cta__orbs" aria-hidden="true">
          <span data-plx="0.1" />
          <span data-plx="0.18" />
        </div>

        <div className="kk-wrap sv-cta__in">
          <span className="kk-eyebrow">Got a project?</span>
          <h2 className="kk-h2">
            Ready to start{" "}
            <span className="kk-serif kk-acid">something?</span>
          </h2>
          <p>
            Tell me what you are building and I will tell you honestly how I can help —
            and what it would take.
          </p>
          <div className="sv-cta__btns">
            <a className="kk-btn kk-btn--lg" href="mailto:karanksxxx@gmail.com?subject=Project Inquiry">
              <FiMail /> Get in touch
            </a>
            <a className="kk-btn kk-btn--ghost kk-btn--lg" href="/contact">
              Contact page <FiArrowUpRight />
            </a>
          </div>
        </div>
      </section>

      <a
        className="kk-wa"
        href="https://wa.me/919041429065"
        target="_blank"
        rel="noreferrer"
        title="Chat on WhatsApp"
      >
        <FaWhatsapp />
      </a>

      <Navbar />
      <Foot />
    </div>
  );
}
